'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { ArrowRightIcon, FunnelIcon, PackageIcon, ReceiptTextIcon, SearchIcon, XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Link } from '@/i18n/navigation';
import { ContentPagination } from '@/components/partials';
import { useTranslatedRoute } from '@/hooks';
import { listMyOrders } from '@/lib/api';
import { AccountOrdersListResponse } from '@/types/order';
const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-blue-100 text-blue-700',
  processing: 'bg-orange-100 text-orange-700',
  shipped: 'bg-indigo-100 text-indigo-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  refunded: 'bg-gray-100 text-gray-700'
};
const statusValues = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded'] as const;
const OrdersPageContent = () => {
  const $ = _c(183);
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    buildRoute
  } = useTranslatedRoute();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get("page") ?? "1";
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const page = Number(t0);
  let t1;
  if ($[2] !== searchParams) {
    t1 = searchParams.get("q") ?? "";
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const q = t1;
  let t2;
  if ($[4] !== searchParams) {
    t2 = searchParams.get("statuses") ?? searchParams.get("status") ?? "";
    $[4] = searchParams;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const statusesQuery = t2;
  let t3;
  if ($[6] !== searchParams) {
    t3 = searchParams.get("createdAtStart") ?? "";
    $[6] = searchParams;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const createdAtStart = t3;
  let t4;
  if ($[8] !== searchParams) {
    t4 = searchParams.get("createdAtEnd") ?? "";
    $[8] = searchParams;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const createdAtEnd = t4;
  let t5;
  if ($[10] !== statusesQuery) {
    t5 = statusesQuery ? statusesQuery.split(",").filter(Boolean) : [];
    $[10] = statusesQuery;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const selectedStatuses = t5;
  const parseQueryDate = _temp;
  const toDateQueryValue = _temp2;
  const [qInput, setQInput] = useState(q);
  const [statusInput, setStatusInput] = useState(selectedStatuses);
  let t6;
  if ($[12] !== createdAtStart) {
    t6 = () => parseQueryDate(createdAtStart);
    $[12] = createdAtStart;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const [fromInput, setFromInput] = useState(t6);
  let t7;
  if ($[14] !== createdAtEnd) {
    t7 = () => parseQueryDate(createdAtEnd);
    $[14] = createdAtEnd;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const [toInput, setToInput] = useState(t7);
  let t8;
  let t9;
  if ($[16] !== createdAtEnd || $[17] !== createdAtStart || $[18] !== q || $[19] !== selectedStatuses) {
    t8 = () => {
      setQInput(q);
      setStatusInput(selectedStatuses);
      setFromInput(parseQueryDate(createdAtStart));
      setToInput(parseQueryDate(createdAtEnd));
    };
    t9 = [q, selectedStatuses, createdAtStart, createdAtEnd];
    $[16] = createdAtEnd;
    $[17] = createdAtStart;
    $[18] = q;
    $[19] = selectedStatuses;
    $[20] = t8;
    $[21] = t9;
  } else {
    t8 = $[20];
    t9 = $[21];
  }
  useEffect(t8, t9);
  let t10;
  if ($[22] !== createdAtEnd || $[23] !== createdAtStart || $[24] !== page || $[25] !== q || $[26] !== statusesQuery) {
    t10 = ["account-orders", page, q, statusesQuery, createdAtStart, createdAtEnd];
    $[22] = createdAtEnd;
    $[23] = createdAtStart;
    $[24] = page;
    $[25] = q;
    $[26] = statusesQuery;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== createdAtEnd || $[29] !== createdAtStart || $[30] !== page || $[31] !== q || $[32] !== selectedStatuses) {
    t11 = () => listMyOrders({
      page,
      size: 10,
      q: q || undefined,
      statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
      createdAtStart: createdAtStart ? `${createdAtStart}T00:00:00` : undefined,
      createdAtEnd: createdAtEnd ? `${createdAtEnd}T23:59:59` : undefined,
      sortBy: "createdAt",
      sort: "desc"
    });
    $[28] = createdAtEnd;
    $[29] = createdAtStart;
    $[30] = page;
    $[31] = q;
    $[32] = selectedStatuses;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11) {
    t12 = {
      queryKey: t10,
      queryFn: t11
    };
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const {
    data,
    isLoading,
    isError,
    refetch
  } = useQuery(t12);
  let t13;
  if ($[37] !== data?.items) {
    t13 = data?.items ?? [];
    $[37] = data?.items;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const items = t13;
  const totalPages = data?.pages ?? 1;
  const setParam = _temp3;
  let t14;
  if ($[39] !== fromInput || $[40] !== pathname || $[41] !== qInput || $[42] !== router || $[43] !== searchParams || $[44] !== statusInput || $[45] !== toInput) {
    t14 = e => {
      e.preventDefault();
      const params_0 = new URLSearchParams(searchParams.toString());
      setParam(params_0, "q", qInput.trim());
      setParam(params_0, "statuses", statusInput.join(","));
      params_0.delete("status");
      setParam(params_0, "createdAtStart", toDateQueryValue(fromInput));
      setParam(params_0, "createdAtEnd", toDateQueryValue(toInput));
      params_0.delete("page");
      router.push(`${pathname}${params_0.toString() ? `?${params_0.toString()}` : ""}`);
    };
    $[39] = fromInput;
    $[40] = pathname;
    $[41] = qInput;
    $[42] = router;
    $[43] = searchParams;
    $[44] = statusInput;
    $[45] = toInput;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  const applyFilters = t14;
  let t15;
  if ($[47] !== pathname || $[48] !== router) {
    t15 = () => {
      setQInput("");
      setStatusInput([]);
      setFromInput(undefined);
      setToInput(undefined);
      router.push(pathname);
    };
    $[47] = pathname;
    $[48] = router;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  const clearFilters = t15;
  let t16;
  if ($[50] !== locale) {
    t16 = value_1 => new Date(value_1).toLocaleString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
    $[50] = locale;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  const formatDate = t16;
  let t17;
  if ($[52] !== locale) {
    t17 = (value_2, currencyCode, currencySymbol) => {
      if (value_2 === null || value_2 === undefined) {
        return "-";
      }
      if (!currencyCode) {
        if (currencySymbol) {
          return `${currencySymbol}${value_2.toFixed(2)}`;
        }
        return value_2.toFixed(2);
      }
      try {
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency: currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value_2);
      } catch {
        return `${value_2.toFixed(2)} ${currencyCode}`;
      }
    };
    $[52] = locale;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  const formatMoney = t17;
  let t18;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <PackageIcon className="h-5 w-5" />;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== t) {
    t19 = t("orders");
    $[55] = t;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== t19) {
    t20 = <h2 className="flex items-center gap-2 text-lg font-semibold">{t18}{t19}</h2>;
    $[57] = t19;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== data?.total || $[60] !== isError || $[61] !== isLoading || $[62] !== t) {
    t21 = !isLoading && !isError && <Badge variant="secondary">{t("orders")}: {data?.total ?? 0}</Badge>;
    $[59] = data?.total;
    $[60] = isError;
    $[61] = isLoading;
    $[62] = t;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== t20 || $[65] !== t21) {
    t22 = <div className="flex flex-wrap items-center justify-between gap-3">{t20}{t21}</div>;
    $[64] = t20;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== t) {
    t23 = t("search");
    $[67] = t;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== t23) {
    t24 = <label htmlFor="orders-search" className="text-muted-foreground text-xs font-medium">{t23}</label>;
    $[69] = t23;
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  let t25;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />;
    $[71] = t25;
  } else {
    t25 = $[71];
  }
  let t26;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = e_0 => setQInput(e_0.target.value);
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  let t27;
  if ($[73] !== t) {
    t27 = t("order_id");
    $[73] = t;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== qInput || $[76] !== t27) {
    t28 = <div className="relative">{t25}<Input id="orders-search" value={qInput} onChange={t26} placeholder={t27} className="pl-9" /></div>;
    $[75] = qInput;
    $[76] = t27;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  let t29;
  if ($[78] !== t24 || $[79] !== t28) {
    t29 = <div className="space-y-1 md:col-span-1 xl:col-span-4">{t24}{t28}</div>;
    $[78] = t24;
    $[79] = t28;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  let t30;
  if ($[81] !== t) {
    t30 = t("status");
    $[81] = t;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  let t31;
  if ($[83] !== t30) {
    t31 = <label htmlFor="orders-statuses" className="text-muted-foreground text-xs font-medium">{t30}</label>;
    $[83] = t30;
    $[84] = t31;
  } else {
    t31 = $[84];
  }
  let t32;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = value_3 => setStatusInput(Array.isArray(value_3) ? value_3 : []);
    $[85] = t32;
  } else {
    t32 = $[85];
  }
  let t33;
  if ($[86] !== t) {
    t33 = t("status");
    $[86] = t;
    $[87] = t33;
  } else {
    t33 = $[87];
  }
  let t34;
  if ($[88] !== t) {
    t34 = t("search");
    $[88] = t;
    $[89] = t34;
  } else {
    t34 = $[89];
  }
  let t35;
  if ($[90] !== t) {
    t35 = t("no_results_found");
    $[90] = t;
    $[91] = t35;
  } else {
    t35 = $[91];
  }
  let t36;
  if ($[92] !== t) {
    t36 = statusValues.map(value_4 => ({
      label: t(`order_status_${value_4}`),
      value: value_4
    }));
    $[92] = t;
    $[93] = t36;
  } else {
    t36 = $[93];
  }
  let t37;
  if ($[94] !== statusInput || $[95] !== t33 || $[96] !== t34 || $[97] !== t35 || $[98] !== t36) {
    t37 = <Combobox id="orders-statuses" multiple={true} clearable={true} value={statusInput} onValueChange={t32} placeholder={t33} searchPlaceholder={t34} emptyText={t35} className="w-full" options={t36} />;
    $[94] = statusInput;
    $[95] = t33;
    $[96] = t34;
    $[97] = t35;
    $[98] = t36;
    $[99] = t37;
  } else {
    t37 = $[99];
  }
  let t38;
  if ($[100] !== t31 || $[101] !== t37) {
    t38 = <div className="space-y-1 md:col-span-1 xl:col-span-3">{t31}{t37}</div>;
    $[100] = t31;
    $[101] = t37;
    $[102] = t38;
  } else {
    t38 = $[102];
  }
  let t39;
  if ($[103] !== t) {
    t39 = t("start_date");
    $[103] = t;
    $[104] = t39;
  } else {
    t39 = $[104];
  }
  let t40;
  if ($[105] !== t39) {
    t40 = <label htmlFor="orders-created-at-start" className="text-muted-foreground text-xs font-medium">{t39}</label>;
    $[105] = t39;
    $[106] = t40;
  } else {
    t40 = $[106];
  }
  let t41;
  if ($[107] !== t) {
    t41 = t("start_date");
    $[107] = t;
    $[108] = t41;
  } else {
    t41 = $[108];
  }
  let t42;
  if ($[109] !== fromInput || $[110] !== locale || $[111] !== t41) {
    t42 = <DatePicker id="orders-created-at-start" value={fromInput} onChange={setFromInput} locale={locale} className="w-full" placeholder={t41} />;
    $[109] = fromInput;
    $[110] = locale;
    $[111] = t41;
    $[112] = t42;
  } else {
    t42 = $[112];
  }
  let t43;
  if ($[113] !== t40 || $[114] !== t42) {
    t43 = <div className="space-y-1 md:col-span-1 xl:col-span-2">{t40}{t42}</div>;
    $[113] = t40;
    $[114] = t42;
    $[115] = t43;
  } else {
    t43 = $[115];
  }
  let t44;
  if ($[116] !== t) {
    t44 = t("end_date");
    $[116] = t;
    $[117] = t44;
  } else {
    t44 = $[117];
  }
  let t45;
  if ($[118] !== t44) {
    t45 = <label htmlFor="orders-created-at-end" className="text-muted-foreground -pointer text-xs font-medium">{t44}</label>;
    $[118] = t44;
    $[119] = t45;
  } else {
    t45 = $[119];
  }
  let t46;
  if ($[120] !== t) {
    t46 = t("end_date");
    $[120] = t;
    $[121] = t46;
  } else {
    t46 = $[121];
  }
  let t47;
  if ($[122] !== locale || $[123] !== t46 || $[124] !== toInput) {
    t47 = <DatePicker id="orders-created-at-end" value={toInput} onChange={setToInput} locale={locale} className="w-full" placeholder={t46} />;
    $[122] = locale;
    $[123] = t46;
    $[124] = toInput;
    $[125] = t47;
  } else {
    t47 = $[125];
  }
  let t48;
  if ($[126] !== t45 || $[127] !== t47) {
    t48 = <div className="space-y-1 md:col-span-1 xl:col-span-2">{t45}{t47}</div>;
    $[126] = t45;
    $[127] = t47;
    $[128] = t48;
  } else {
    t48 = $[128];
  }
  let t49;
  if ($[129] !== t) {
    t49 = t("apply");
    $[129] = t;
    $[130] = t49;
  } else {
    t49 = $[130];
  }
  let t50;
  if ($[131] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <FunnelIcon className="h-4 w-4" />;
    $[131] = t50;
  } else {
    t50 = $[131];
  }
  let t51;
  if ($[132] !== t49) {
    t51 = <Button type="submit" size="icon" className="shrink-0" title={t49}>{t50}</Button>;
    $[132] = t49;
    $[133] = t51;
  } else {
    t51 = $[133];
  }
  let t52;
  if ($[134] !== t) {
    t52 = t("clear");
    $[134] = t;
    $[135] = t52;
  } else {
    t52 = $[135];
  }
  let t53;
  if ($[136] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <XIcon className="h-4 w-4" />;
    $[136] = t53;
  } else {
    t53 = $[136];
  }
  let t54;
  if ($[137] !== clearFilters || $[138] !== t52) {
    t54 = <Button type="button" variant="outline" size="icon" onClick={clearFilters} title={t52}>{t53}</Button>;
    $[137] = clearFilters;
    $[138] = t52;
    $[139] = t54;
  } else {
    t54 = $[139];
  }
  let t55;
  if ($[140] !== t51 || $[141] !== t54) {
    t55 = <div className="mt-2 flex items-end justify-end gap-2 md:col-span-2 xl:col-span-1">{t51}{t54}</div>;
    $[140] = t51;
    $[141] = t54;
    $[142] = t55;
  } else {
    t55 = $[142];
  }
  let t56;
  if ($[143] !== applyFilters || $[144] !== t29 || $[145] !== t38 || $[146] !== t43 || $[147] !== t48 || $[148] !== t55) {
    t56 = <form onSubmit={applyFilters} className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-12">{t29}{t38}{t43}{t48}{t55}</form>;
    $[143] = applyFilters;
    $[144] = t29;
    $[145] = t38;
    $[146] = t43;
    $[147] = t48;
    $[148] = t55;
    $[149] = t56;
  } else {
    t56 = $[149];
  }
  let t57;
  if ($[150] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <Separator />;
    $[150] = t57;
  } else {
    t57 = $[150];
  }
  let t58;
  if ($[151] !== isLoading) {
    t58 = isLoading && <div className="space-y-3">{Array.from({
        length: 4
      }).map(_temp4)}</div>;
    $[151] = isLoading;
    $[152] = t58;
  } else {
    t58 = $[152];
  }
  let t59;
  if ($[153] !== isError || $[154] !== isLoading || $[155] !== refetch || $[156] !== t) {
    t59 = isError && !isLoading && <div className="flex flex-col items-center gap-3 py-10"><p className="text-destructive text-sm">{t("something_went_wrong")}</p><Button variant="outline" size="sm" onClick={() => refetch()}>{t("try_again")}</Button></div>;
    $[153] = isError;
    $[154] = isLoading;
    $[155] = refetch;
    $[156] = t;
    $[157] = t59;
  } else {
    t59 = $[157];
  }
  let t60;
  if ($[158] !== buildRoute || $[159] !== isError || $[160] !== isLoading || $[161] !== items || $[162] !== t) {
    t60 = !isLoading && !isError && items.length === 0 && <div className="flex flex-col items-center gap-3 py-12 text-center"><ReceiptTextIcon className="text-muted-foreground h-10 w-10" /><p className="text-muted-foreground text-sm">{t("no_orders_yet")}</p><Button variant="outline" asChild={true}><Link href={buildRoute("/products")}>{t("continue_shopping")}</Link></Button></div>;
    $[158] = buildRoute;
    $[159] = isError;
    $[160] = isLoading;
    $[161] = items;
    $[162] = t;
    $[163] = t60;
  } else {
    t60 = $[163];
  }
  let t61;
  if ($[164] !== buildRoute || $[165] !== formatDate || $[166] !== formatMoney || $[167] !== isError || $[168] !== isLoading || $[169] !== items || $[170] !== t) {
    t61 = !isLoading && !isError && items.length > 0 && <div className="space-y-3">{items.map(order => {
        const normalizedStatus = order.status.toLowerCase();
        const hasDiscount = (order.discountAmount ?? 0) > 0;
        const hasTax = (order.taxTotal ?? 0) > 0;
        return <div key={order.id} className="rounded-lg border p-4"><div className="mb-3 flex flex-wrap items-center justify-between gap-2"><div className="min-w-0"><p className="text-muted-foreground text-xs">{t("order_id")}</p><p className="truncate font-mono text-sm font-semibold">{order.id}</p></div><Badge className={statusColors[normalizedStatus] ?? "bg-gray-100 text-gray-700"}>{t(`order_status_${normalizedStatus}`)}</Badge></div><div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3 lg:grid-cols-6"><div><p className="text-muted-foreground text-xs">{t("order_date")}</p><p className="font-medium">{formatDate(order.createdAt)}</p></div><div><p className="text-muted-foreground text-xs">{t("order_items")}</p><p className="font-medium">{order.itemCount}</p></div><div><p className="text-muted-foreground text-xs">{t("subtotal")}</p><p className="font-medium">{formatMoney(order.subtotal, order.currencyCode, order.currencySymbol)}</p></div><div><p className="text-muted-foreground text-xs">{t("tax")}</p><p className="font-medium">{hasTax ? formatMoney(order.taxTotal, order.currencyCode, order.currencySymbol) : "-"}</p></div><div><p className="text-muted-foreground text-xs">{t("discount")}</p><p className={`font-medium ${hasDiscount ? "text-green-700" : ""}`}>{hasDiscount ? formatMoney(order.discountAmount, order.currencyCode, order.currencySymbol) : "-"}</p></div><div><p className="text-muted-foreground text-xs">{t("total")}</p><p className="font-medium">{formatMoney(order.total, order.currencyCode, order.currencySymbol)}</p></div></div><div className="mt-3 border-t pt-3 text-right"><Button asChild={true} variant="outline" size="sm" className="border-brand text-brand hover:bg-brand/5"><Link href={buildRoute("/orders", order.id)}><span>{t("view_details")}</span><ArrowRightIcon /></Link></Button></div></div>;
      })}</div>;
    $[164] = buildRoute;
    $[165] = formatDate;
    $[166] = formatMoney;
    $[167] = isError;
    $[168] = isLoading;
    $[169] = items;
    $[170] = t;
    $[171] = t61;
  } else {
    t61 = $[171];
  }
  let t62;
  if ($[172] !== page || $[173] !== totalPages) {
    t62 = <ContentPagination currentPage={page} totalPages={totalPages} />;
    $[172] = page;
    $[173] = totalPages;
    $[174] = t62;
  } else {
    t62 = $[174];
  }
  let t63;
  if ($[175] !== t22 || $[176] !== t56 || $[177] !== t58 || $[178] !== t59 || $[179] !== t60 || $[180] !== t61 || $[181] !== t62) {
    t63 = <div className="flex w-full flex-col gap-6 rounded-lg bg-white p-6 shadow-sm">{t22}{t56}{t57}{t58}{t59}{t60}{t61}{t62}</div>;
    $[175] = t22;
    $[176] = t56;
    $[177] = t58;
    $[178] = t59;
    $[179] = t60;
    $[180] = t61;
    $[181] = t62;
    $[182] = t63;
  } else {
    t63 = $[182];
  }
  return t63;
};
export default OrdersPageContent;
function _temp(value) {
  return value ? new Date(`${value}T00:00:00`) : undefined;
}
function _temp2(date) {
  if (!date) {
    return "";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
function _temp3(params, key, value_0) {
  if (value_0) {
    params.set(key, value_0);
  } else {
    params.delete(key);
  }
}
function _temp4(_, i) {
  return <div key={i} className="rounded-lg border p-4"><div className="mb-3 flex items-center justify-between gap-3"><Skeleton className="h-4 w-44" /><Skeleton className="h-6 w-24 rounded-full" /></div><div className="grid grid-cols-1 gap-2 sm:grid-cols-4"><Skeleton className="h-4 w-24" /><Skeleton className="h-4 w-20" /><Skeleton className="h-4 w-28" /><Skeleton className="h-4 w-24" /></div></div>;
}